// src/pages/donations/page.tsx
import { useState } from "react";
import { useDonations } from "@/lib/api/hooks";
import { PageHeader } from "@/components/layout/page-header";
import { Plus, Calendar, MapPin } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { DonationForm } from "./components/donation-form";
import { DonationListItem } from "./components/donation-list-item";
import { EmptyState } from "@/components/ui/empty-state";
import { Heart } from 'lucide-react';
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Donation } from "@/lib/api/types";
import { PermissionGuard } from "@/components/guards/PermissionGuard";

// Add ViewDialog component
function ViewDialog({ donation, isOpen, onClose }: {
  donation: Donation | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!donation) return null;

  return (
    <Modal
      title="Donation Details"
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="space-y-6">
        {/* Hero Image */}
        {donation.imageUrl ? (
          <div className="relative w-full h-64 rounded-lg overflow-hidden border border-border">
            <img
              src={donation.imageUrl}
              alt={donation.title}
              className="object-cover w-full h-full"
            />
          </div>
        ) : (
          <div className="relative w-full h-64 rounded-lg overflow-hidden border border-border bg-gradient-to-br from-rose-500/10 to-rose-500/5 flex items-center justify-center">
            <Heart className="w-24 h-24 text-rose-500/20" />
          </div>
        )}

        {/* Title */}
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">{donation.title}</h2>
          <div className="h-1 w-20 bg-rose-500 rounded-full"></div>
        </div>

        {/* Meta Info Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-lg border border-border bg-card">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wide">Date</span>
            </div>
            <p className="text-sm font-semibold text-foreground">
              {format(new Date(donation.date), "MMMM d, yyyy")}
            </p>
          </div>

          <div className="p-4 rounded-lg border border-border bg-card">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <MapPin className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wide">Location</span>
            </div>
            <p className="text-sm font-semibold text-foreground">
              {donation.location}
            </p>
          </div>
        </div>

        {/* Description */}
        <div className="p-4 rounded-lg border border-border bg-card/50">
          <h3 className="text-sm font-medium uppercase tracking-wide text-muted-foreground mb-3">
            Description
          </h3>
          <p className="text-foreground leading-relaxed whitespace-pre-wrap">
            {donation.description}
          </p>
        </div>

        {/* Timestamps */}
        <div className="pt-4 border-t border-border">
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <div>
              <span className="font-medium">Created:</span>{' '}
              {format(new Date(donation.createdAt), "MMM d, yyyy 'at' h:mm a")}
            </div>
            {donation.updatedAt !== donation.createdAt && (
              <div>
                <span className="font-medium">Updated:</span>{' '}
                {format(new Date(donation.updatedAt), "MMM d, yyyy 'at' h:mm a")}
              </div>
            )}
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-end pt-2">
          <Button onClick={onClose} className="bg-rose-500 hover:bg-rose-600">
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default function DonationsPage() {
  const { donations, isLoading, create, update, delete: deleteDonation } = useDonations();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedDonation, setSelectedDonation] = useState<Donation | null>(null);

  const handleCreateUpdate = async (formData: FormData) => {
    try {
      if (selectedDonation) {
        await update({ id: selectedDonation.id, data: formData });
      } else {
        await create(formData);
      }
      setIsModalOpen(false);
      setSelectedDonation(null);
    } catch (error) {
      // Error is handled by the API hook
    }
  };

  const handleDelete = async () => {
    if (!selectedDonation) return;
    try {
      await deleteDonation(selectedDonation.id);
      setIsDeleteDialogOpen(false);
      setSelectedDonation(null);
    } catch (error) {
      // Error is handled by the API hook
    }
  };

  if (isLoading) {
    return <div className="text-foreground">loading....</div>;
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Donations"
        action={
          <PermissionGuard resource="donations" action="create">
            <Button onClick={() => setIsModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Donation
            </Button>
          </PermissionGuard>
        }
      />

      {donations.length === 0 ? (
        <EmptyState
          icon={Heart}
          title="No donations yet"
          description="Start by creating your first donation."
          action={
            <PermissionGuard resource="donations" action="create">
              <Button onClick={() => setIsModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Donation
              </Button>
            </PermissionGuard>
          }
        />
      ) : (
        <div className="space-y-4">
          {donations.map((donation) => (
            <DonationListItem
              key={donation.id}
              donation={donation}
              onView={(donation) => {
                setSelectedDonation(donation);
                setIsViewDialogOpen(true);
              }}
              onEdit={(donation) => {
                setSelectedDonation(donation);
                setIsModalOpen(true);
              }}
              onDelete={(donation) => {
                setSelectedDonation(donation);
                setIsDeleteDialogOpen(true);
              }}
            />
          ))}
        </div>
      )}

      <Modal
        title={selectedDonation ? "Edit Donation" : "Add Donation"}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDonation(null);
        }}
      >
        <DonationForm
          initialData={selectedDonation}
          onSubmit={handleCreateUpdate}
        />
      </Modal>

      <ViewDialog
        donation={selectedDonation}
        isOpen={isViewDialogOpen}
        onClose={() => {
          setIsViewDialogOpen(false);
          setSelectedDonation(null);
        }}
      />

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Donation"
        description="Are you sure you want to delete this donation? This action cannot be undone."
      />
    </div>
  );
}